import {
  forwardsApi,
  forwardsErrorMessage,
  isDomainError,
  isNotFoundError,
  planVersionConflict,
} from "@/forwards2/forwardsApi";
import type {
  CommitmentPoolRow,
  DealMetadataRequest,
  DealResponse,
  EligibilityCriteriaRequest,
  EscrowAttributionRow,
  EscrowMoveResult,
  EscrowPullRequest,
  EscrowReturnRequest,
  PapMonitoringResponse,
  PapScheduleUpdateRequest,
  PlanApplyResult,
  PlanStateRequest,
  ProducerPricesRequest,
  DeliveryNotesRequest,
} from "@/forwards2/forwardsTypes";
import type { PeriodWidth } from "@/forwards2/periodUtils";
import { defineStore } from "pinia";
import { computed, readonly, ref, shallowRef } from "vue";

/** Which button is spinning. One at a time — the page disables the rest while an action runs. */
export type DealAction =
  | "metadata"
  | "criteria"
  | "confirm"
  | "unconfirm"
  | "cancel"
  | "offering_sheet"
  | "plan"
  | "move"
  | "narrow"
  | "resize"
  | "add_commitment"
  | "delete_backing"
  | "escrow_return"
  | "escrow_pull"
  | "pap_schedule"
  | "producer_prices"
  | "delivery_notes";

/**
 * How a save that carries `plan_version` ended. `conflict` means someone else saved meanwhile: the
 * deal (and the pay-as-produced monitoring) has been read again, no refusal is shown, and the
 * editor merges its draft onto what it now renders.
 */
export type VersionedSaveOutcome = "saved" | "conflict" | "failed";

/**
 * The deal page's state.
 *
 * Every mutating route answers with the whole `DealResponse`, so each action replaces `deal` from
 * its own response rather than re-fetching: the page can never render a plan and a set of badges
 * that were computed at different moments. The one exception is actions that overlap: their
 * answers can land in any order, so once the last of them settles the deal is read again.
 *
 * Actions never throw. A 4xx carries a sentence meant for the user and lands in
 * `actionErrorMessage`, which the surface that triggered it renders inline; anything else lands in
 * `actionError` for the view to hand to `globalErrorHandler`.
 */
export const useDealStore = defineStore("forwards2Deal", () => {
  const dealId = ref<number | null>(null);
  const deal = ref<DealResponse | null>(null);
  const loading = ref(false);
  const notFound = ref(false);
  // `shallowRef`: a foreign error object put in a deep `ref` comes back as a reactive proxy, which
  // breaks identity checks and `instanceof` for the code that has to classify it.
  const loadError = shallowRef<unknown>(null);

  const busyAction = ref<DealAction | null>(null);
  const actionError = shallowRef<unknown>(null);
  const actionErrorMessage = ref<string | null>(null);
  // Which action `actionErrorMessage` belongs to, so only the surface that ran it renders the
  // sentence; null for the background reads, whose failures have no surface but the page header.
  const failedAction = ref<DealAction | null>(null);

  const planPreview = ref<PlanApplyResult | null>(null);

  const papMonitoring = ref<PapMonitoringResponse | null>(null);
  const papMonitoringLoading = ref(false);

  const escrowAttributions = ref<EscrowAttributionRow[]>([]);
  const escrowAttributionsLoading = ref(false);

  const commitmentPools = ref<CommitmentPoolRow[]>([]);
  const commitmentPoolsLoading = ref(false);

  // The pool behind the slice the resize dialog grows, read for its sales strategy figures.
  const resizePool = ref<CommitmentPoolRow | null>(null);

  const deliveries = computed(() => deal.value?.deliveries ?? []);
  const commitmentGroups = computed(() => deal.value?.commitments ?? []);
  const freeEscrow = computed(() => deal.value?.free_escrow ?? []);
  const flags = computed(() => deal.value?.flags ?? null);
  const capabilities = computed(() => deal.value?.capabilities ?? null);
  const isPayAsProduced = computed(
    () => deal.value?.type === "pay_as_produced"
  );

  /** Every slice of the plan, flattened — the source of the plan summary's totals. */
  const slices = computed(() =>
    deliveries.value.flatMap((delivery) => delivery.slices)
  );

  const plannedMwh = computed(() =>
    deliveries.value.reduce((sum, delivery) => sum + delivery.planned_mwh, 0)
  );
  const escrowedMwh = computed(() =>
    deliveries.value.reduce((sum, delivery) => sum + delivery.escrowed_mwh, 0)
  );
  const committedMwh = computed(() =>
    slices.value
      .filter((slice) => !slice.is_open)
      .reduce((sum, slice) => sum + slice.volume_mwh, 0)
  );
  const openMwh = computed(() => flags.value?.open_volume_mwh ?? 0);

  /** The offering sheets generated so far, newest last; the links column is a comma-separated history. */
  const offeringSheetUrls = computed(() =>
    (deal.value?.volumes_specification_links ?? "")
      .split(",")
      .map((link) => link.trim())
      .filter((link) => link.length > 0)
  );

  // The view is reused across an address-bar change of deal id, so a slow answer for the previous
  // deal can land after the next one's: only the latest load may write.
  let loadGeneration = 0;

  /** Loads the deal page. Never throws — read `notFound` and `loadError`. */
  const load = async (id: number) => {
    const generation = ++loadGeneration;
    if (id !== dealId.value) resetDealScopedState();
    dealId.value = id;
    loading.value = true;
    notFound.value = false;
    try {
      const response = await forwardsApi.getDeal(id);
      if (generation !== loadGeneration) return;
      deal.value = response.data;
      loadError.value = null;
    } catch (caught) {
      if (generation !== loadGeneration) return;
      deal.value = null;
      notFound.value = isNotFoundError(caught);
      loadError.value = notFound.value ? null : caught;
    } finally {
      if (generation === loadGeneration) loading.value = false;
    }
  };

  /**
   * What belongs to one deal and must not carry over to the next; `refresh()` keeps it, so an open editor survives.
   * The deal itself goes too, so the page shows its loading state rather than the deal it left.
   *
   * It also retires every background read still in flight for the deal being left: those can no
   * longer write, so their loading flags are cleared here rather than by them.
   */
  const resetDealScopedState = () => {
    deal.value = null;
    clearActionError();
    clearPlanPreview();
    papMonitoringGeneration++;
    papMonitoring.value = null;
    papMonitoringLoading.value = false;
    escrowAttributionsGeneration++;
    escrowAttributions.value = [];
    escrowAttributionsLoading.value = false;
    commitmentPoolsGeneration++;
    commitmentPools.value = [];
    commitmentPoolsLoading.value = false;
    resizePoolGeneration++;
    resizePool.value = null;
  };

  const refresh = async () => {
    if (dealId.value !== null) await load(dealId.value);
  };

  const clearActionError = () => {
    actionError.value = null;
    actionErrorMessage.value = null;
    failedAction.value = null;
  };

  /**
   * Records a failure for the surface that caused it. `action` is null for a background read, which
   * must also clear the action an earlier failure named — otherwise its message would land in that
   * action's modal instead of the page header.
   */
  const reportError = (caught: unknown, action: DealAction | null) => {
    actionErrorMessage.value = forwardsErrorMessage(caught);
    actionError.value = isDomainError(caught) ? null : caught;
    failedAction.value = action;
  };

  /**
   * Runs one mutating call, replacing the deal from whatever it answered with.
   *
   * @returns the call's own result, or null when it failed — the caller decides what a failure
   * means for its modal (stay open with the message, usually).
   */
  // Not every trigger is disabled while an action runs, so two can overlap; each clears the spinner
  // only if no later action has taken it over.
  let runSequence = 0;
  // Neither of two overlapping answers can tell which is newer: the server may have served the later
  // request first, so "latest started wins" would keep the stale one. Counting what is in flight lets
  // the last to settle read the deal again instead; a re-read still in flight counts too, since an
  // action it overlaps may have written after the server answered it.
  let runsInFlight = 0;
  let rereadsInFlight = 0;
  let overlapped = false;
  let rereadGeneration = 0;

  const run = async <T>(
    action: DealAction,
    call: () => Promise<{ data: T }>,
    dealOf: (result: T) => DealResponse | null
  ): Promise<T | null> => {
    const runId = ++runSequence;
    if (runsInFlight > 0 || rereadsInFlight > 0) overlapped = true;
    runsInFlight++;
    busyAction.value = action;
    clearActionError();
    // A slow answer can land after the page has moved on to another deal (see `loadGeneration`).
    const generation = loadGeneration;
    try {
      const response = await call();
      const updated = dealOf(response.data);
      if (updated !== null && generation === loadGeneration)
        deal.value = updated;
      return response.data;
    } catch (caught) {
      if (generation !== loadGeneration) return null;
      reportError(caught, action);
      return null;
    } finally {
      if (runId === runSequence) busyAction.value = null;
      runsInFlight--;
      if (runsInFlight === 0 && overlapped) {
        overlapped = false;
        void rereadDeal();
      }
    }
  };

  /**
   * Reads the deal again after overlapping actions, without the spinner or the error states of
   * `load`: the page keeps showing the deal it has, and a failure is reported like any other
   * background read's.
   */
  const rereadDeal = async () => {
    const generation = loadGeneration;
    const reread = ++rereadGeneration;
    rereadsInFlight++;
    try {
      const response = await forwardsApi.getDeal(id());
      if (generation === loadGeneration && reread === rereadGeneration)
        deal.value = response.data;
    } catch (caught) {
      if (generation !== loadGeneration || reread !== rereadGeneration) return;
      reportError(caught, null);
    } finally {
      rereadsInFlight--;
    }
  };

  const asDeal = (result: DealResponse) => result;
  const wrappedDeal = (result: { deal: DealResponse }) => result.deal;

  const saveMetadata = (body: DealMetadataRequest) =>
    run("metadata", () => forwardsApi.updateDealMetadata(id(), body), asDeal);

  const previewCriteria = (body: EligibilityCriteriaRequest) =>
    run(
      "criteria",
      () => forwardsApi.updateDealCriteria(id(), body, { dryRun: true }),
      wrappedDeal
    );

  const saveCriteria = (body: EligibilityCriteriaRequest) =>
    run(
      "criteria",
      () => forwardsApi.updateDealCriteria(id(), body),
      wrappedDeal
    );

  const confirm = () =>
    run("confirm", () => forwardsApi.confirmDeal(id()), asDeal);

  const unconfirm = () =>
    run("unconfirm", () => forwardsApi.unconfirmDeal(id()), asDeal);

  const cancel = () =>
    run("cancel", () => forwardsApi.cancelDeal(id()), asDeal);

  const generateOfferingSheet = () =>
    run(
      "offering_sheet",
      () => forwardsApi.generateOfferingSheet(id()),
      wrappedDeal
    );

  const moveSlice = (
    sliceId: number,
    targetDeliveryId: number,
    volumeMwh: number | null
  ) =>
    run(
      "move",
      () =>
        forwardsApi.moveSlice(id(), sliceId, {
          target_delivery_id: targetDeliveryId,
          volume_mwh: volumeMwh,
        }),
      wrappedDeal
    );

  const narrowSlice = (sliceId: number, width: PeriodWidth) =>
    run(
      "narrow",
      () => forwardsApi.narrowSlice(id(), sliceId, width),
      wrappedDeal
    );

  const resizeCommitment = (
    sliceId: number,
    volumeMwh: number,
    ignoreSalesStrategy = false
  ) =>
    run(
      "resize",
      () =>
        forwardsApi.resizeCommitment(id(), sliceId, volumeMwh, {
          ignoreSalesStrategy,
        }),
      wrappedDeal
    );

  const addCommitment = (
    poolId: number,
    takes: Record<number, number>,
    ignoreSalesStrategy = false
  ) =>
    run(
      "add_commitment",
      () =>
        forwardsApi.addCommitment(id(), {
          pool_id: poolId,
          takes,
          ...(ignoreSalesStrategy
            ? { dangerous_ignore_sales_strategy: true }
            : {}),
        }),
      wrappedDeal
    );

  const deleteBacking = (sliceId: number) =>
    run(
      "delete_backing",
      () => forwardsApi.deleteBacking(id(), sliceId),
      asDeal
    );

  /**
   * Runs a save that carries `plan_version` and tells a stale one apart from any other refusal:
   * the refusal is not shown, and the deal is read again for the editor to merge onto.
   */
  const runVersioned = async <T>(
    action: DealAction,
    call: () => Promise<{ data: T }>,
    dealOf: (result: T) => DealResponse | null
  ): Promise<VersionedSaveOutcome> => {
    let conflicted = false;
    const result = await run(
      action,
      async () => {
        try {
          return await call();
        } catch (caught) {
          conflicted = planVersionConflict(caught) !== null;
          throw caught;
        }
      },
      dealOf
    );
    if (result !== null) return "saved";
    if (!conflicted || failedAction.value !== action) return "failed";
    clearActionError();
    await refresh();
    return "conflict";
  };

  /** Admin only. The map is the delivery's whole set of prices — a company left out is cleared. */
  const saveProducerPrices = (
    deliveryId: number,
    request: ProducerPricesRequest
  ) =>
    runVersioned(
      "producer_prices",
      () => forwardsApi.saveProducerPrices(id(), deliveryId, request),
      asDeal
    );

  /** Admin only. The delivery's staff notes, whole: blank clears them. */
  const saveDeliveryNotes = (
    deliveryId: number,
    request: DeliveryNotesRequest
  ) =>
    run(
      "delivery_notes",
      () => forwardsApi.saveDeliveryNotes(id(), deliveryId, request),
      asDeal
    );

  /** The real plan save. All-or-nothing: on a domain error nothing was written. */
  const savePlan = async (state: PlanStateRequest) => {
    const result = await run(
      "plan",
      () => forwardsApi.savePlan(id(), state),
      wrappedDeal
    );
    if (result !== null) clearPlanPreview();
    return result;
  };

  // A dry run cannot be cancelled once sent; a save or discard that lands during its round trip
  // must not have its cleared preview repopulated by the late answer.
  let previewGeneration = 0;

  /**
   * The editor's live preview: the same state, applied and rolled back.
   *
   * The deal itself is deliberately left alone — a preview must not move the page underneath the
   * form the user is still editing. That also keeps `plan_version` steady while the form is open.
   */
  const previewPlan = async (state: PlanStateRequest) => {
    const generation = ++previewGeneration;
    clearActionError();
    try {
      const response = await forwardsApi.savePlan(id(), state, {
        dryRun: true,
      });
      if (generation !== previewGeneration) return null;
      planPreview.value = response.data;
      return response.data;
    } catch (caught) {
      if (generation !== previewGeneration) return null;
      planPreview.value = null;
      reportError(caught, "plan");
      return null;
    }
  };

  const clearPlanPreview = () => {
    previewGeneration++;
    planPreview.value = null;
  };

  // One generation per background read: only its latest request may write or clear its loading
  // flag, and leaving the deal (`resetDealScopedState`) retires whatever is still in flight.
  let commitmentPoolsGeneration = 0;
  let resizePoolGeneration = 0;
  let escrowAttributionsGeneration = 0;
  let papMonitoringGeneration = 0;

  /** The add-commitment type-ahead. Never throws — an empty list is what a failure looks like. */
  const loadCommitmentPools = async (query: string) => {
    const generation = ++commitmentPoolsGeneration;
    commitmentPoolsLoading.value = true;
    try {
      const response = await forwardsApi.listCommitmentPools(id(), query);
      if (generation !== commitmentPoolsGeneration) return;
      commitmentPools.value = response.data;
    } catch (caught) {
      if (generation !== commitmentPoolsGeneration) return;
      commitmentPools.value = [];
      reportError(caught, null);
    } finally {
      if (generation === commitmentPoolsGeneration)
        commitmentPoolsLoading.value = false;
    }
  };

  /**
   * The one pool a resize would grow from, with its strategy cap and availability. Never throws:
   * null is what a failure looks like, and the resize then goes without the override banner — the
   * backend still refuses a grow beyond the strategy.
   */
  const loadResizePool = async (poolId: number) => {
    const generation = ++resizePoolGeneration;
    resizePool.value = null;
    try {
      const response = await forwardsApi.listCommitmentPools(id(), "", {
        poolId,
      });
      if (generation !== resizePoolGeneration) return;
      resizePool.value =
        response.data.find((row) => row.pool_id === poolId) ?? null;
    } catch (caught) {
      if (generation !== resizePoolGeneration) return;
      reportError(caught, null);
    }
  };

  const loadEscrowAttributions = async () => {
    const generation = ++escrowAttributionsGeneration;
    escrowAttributionsLoading.value = true;
    try {
      const response = await forwardsApi.listEscrowAttributions(id());
      if (generation !== escrowAttributionsGeneration) return;
      escrowAttributions.value = response.data.rows;
    } catch (caught) {
      if (generation !== escrowAttributionsGeneration) return;
      escrowAttributions.value = [];
      reportError(caught, null);
    } finally {
      if (generation === escrowAttributionsGeneration)
        escrowAttributionsLoading.value = false;
    }
  };

  /**
   * Returns escrow to its owners, then reloads the deal.
   *
   * The route answers with per-item results rather than a deal, and a partial failure is a normal
   * outcome, so the caller renders `items` and the page reads the new state from a fresh fetch.
   */
  const returnEscrow = async (
    body: EscrowReturnRequest
  ): Promise<EscrowMoveResult | null> => {
    const result = await run<EscrowMoveResult>(
      "escrow_return",
      () => forwardsApi.returnEscrow(id(), body),
      () => null
    );
    if (result !== null) await refresh();
    return result;
  };

  const pullEscrow = async (
    body: EscrowPullRequest
  ): Promise<EscrowMoveResult | null> => {
    const result = await run<EscrowMoveResult>(
      "escrow_pull",
      () => forwardsApi.pullEscrow(id(), body),
      () => null
    );
    if (result !== null) await refresh();
    return result;
  };

  const loadPapMonitoring = async () => {
    const generation = ++papMonitoringGeneration;
    papMonitoringLoading.value = true;
    try {
      const response = await forwardsApi.getPapMonitoring(id());
      if (generation !== papMonitoringGeneration) return;
      papMonitoring.value = response.data;
    } catch (caught) {
      if (generation !== papMonitoringGeneration) return;
      papMonitoring.value = null;
      reportError(caught, null);
    } finally {
      if (generation === papMonitoringGeneration)
        papMonitoringLoading.value = false;
    }
  };

  /** The nominations, the tolerance and the months' sell prices, in one write. */
  const savePapSchedule = async (body: PapScheduleUpdateRequest) => {
    const outcome = await runVersioned(
      "pap_schedule",
      () => forwardsApi.updatePapSchedule(id(), body),
      asDeal
    );
    if (outcome !== "failed") await loadPapMonitoring();
    return outcome;
  };

  function id(): number {
    if (dealId.value === null)
      throw new Error("useDealStore: load() must run before any action");
    return dealId.value;
  }

  return {
    dealId: readonly(dealId),
    deal: computed(() => deal.value),
    loading: readonly(loading),
    notFound: readonly(notFound),
    busyAction: readonly(busyAction),
    actionErrorMessage: readonly(actionErrorMessage),
    failedAction: readonly(failedAction),
    planPreview: computed(() => planPreview.value),
    papMonitoring: computed(() => papMonitoring.value),
    papMonitoringLoading: readonly(papMonitoringLoading),
    escrowAttributions: computed(() => escrowAttributions.value),
    escrowAttributionsLoading: readonly(escrowAttributionsLoading),
    commitmentPools: computed(() => commitmentPools.value),
    commitmentPoolsLoading: readonly(commitmentPoolsLoading),
    resizePool: computed(() => resizePool.value),
    deliveries,
    commitmentGroups,
    freeEscrow,
    flags,
    capabilities,
    isPayAsProduced,
    slices,
    plannedMwh,
    escrowedMwh,
    committedMwh,
    openMwh,
    offeringSheetUrls,
    // Computeds rather than `readonly()`: a readonly proxy of a foreign error object would break
    // the same identity checks the `shallowRef`s above protect.
    loadError: computed(() => loadError.value),
    actionError: computed(() => actionError.value),
    load,
    refresh,
    clearActionError,
    saveMetadata,
    previewCriteria,
    saveCriteria,
    confirm,
    unconfirm,
    cancel,
    generateOfferingSheet,
    moveSlice,
    narrowSlice,
    resizeCommitment,
    addCommitment,
    deleteBacking,
    saveProducerPrices,
    saveDeliveryNotes,
    savePlan,
    previewPlan,
    clearPlanPreview,
    loadCommitmentPools,
    loadResizePool,
    loadEscrowAttributions,
    returnEscrow,
    pullEscrow,
    loadPapMonitoring,
    savePapSchedule,
  };
});
